import React, { useEffect, useState } from 'react'
import { UseMutationResult } from '@tanstack/react-query'

import {
    Box,
    Button,
    Chip,
    IconButton,
    InputAdornment,
    Modal,
    Paper,
    Slide,
    TextField,
    Tooltip,
    Typography,
    makeStyles,
    Theme,
} from '@material-ui/core'
import {
    Close as CloseIcon,
    Delete as DeleteIcon,
    AttachFile as AttachFileIcon,
    CloudUpload as UploadIcon,
    Event as EventIcon,
    LocationOn as LocationIcon,
    Schedule as ScheduleIcon,
    Public as PublishIcon,
    Edit as DraftIcon,
    Send as SendIcon,
    MailOutline as MailIcon,
} from '@material-ui/icons'
import {
    KeyboardDatePicker,
    KeyboardDateTimePicker,
} from '@material-ui/pickers'

import { DateTime } from 'luxon'

import {
    PropertyEvent,
    PropertyEventAttachment,
    EventMessageSummary,
    CreatePropertyEventInput,
    UpdatePropertyEventInput,
    DraftMessageResponse,
    SendEventMessageInput,
} from '../../models/PropertyEvent'
import { TenantMessage } from '../../models/TenantMessage'
import { EventAnnouncementModal } from './EventAnnouncementModal'

const BORDER = '#e6e9ed'
const SURFACE = '#f6f8fa'

const useStyles = makeStyles((theme: Theme) => ({
    paper: {
        borderRadius: 16,
        overflow: 'hidden',
        outline: 'none',
        boxShadow:
            '0 24px 48px -12px rgba(0,0,0,0.25), 0 0 0 1px rgba(0,0,0,0.04)',
        width: '90vw',
        [theme.breakpoints.up('sm')]: {
            width: '80vw',
            maxWidth: 680,
        },
        maxHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
    },
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 3),
        borderBottom: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
    },
    headerIcon: {
        width: 40,
        height: 40,
        borderRadius: 12,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        backgroundColor: `${theme.palette.primary.main}14`,
        color: theme.palette.primary.main,
        flexShrink: 0,
    },
    body: {
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        backgroundColor: SURFACE,
        padding: theme.spacing(2.5),
    },
    section: {
        backgroundColor: '#fff',
        border: `1px solid ${BORDER}`,
        borderRadius: 12,
        padding: theme.spacing(2.5),
        marginBottom: theme.spacing(2),
    },
    sectionTitle: {
        fontWeight: 700,
        fontSize: '0.85rem',
        color: theme.palette.text.secondary,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        marginBottom: theme.spacing(1.5),
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: theme.spacing(1.5),
        padding: theme.spacing(2, 3),
        borderTop: `1px solid ${BORDER}`,
        backgroundColor: '#fff',
    },
    primaryButton: {
        textTransform: 'none',
        fontWeight: 600,
        boxShadow: 'none',
        borderRadius: 8,
        padding: theme.spacing(1, 2.5),
        '&:hover': {
            boxShadow: 'none',
        },
    },
    cancelButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
    },
    deleteButton: {
        textTransform: 'none',
        fontWeight: 600,
        borderRadius: 8,
        color: theme.palette.error.main,
        '&:hover': {
            backgroundColor: 'rgba(244, 67, 54, 0.04)',
        },
    },
    statusToggle: {
        display: 'flex',
        gap: theme.spacing(1),
    },
    statusOption: {
        flex: 1,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: theme.spacing(1),
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: theme.spacing(1.25, 1.5),
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
            borderColor: '#b0b8c1',
        },
    },
    statusOptionActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}08`,
    },
    allDayToggle: {
        display: 'flex',
        alignItems: 'center',
        gap: theme.spacing(1),
        border: `1.5px solid ${BORDER}`,
        borderRadius: 10,
        padding: theme.spacing(1, 1.5),
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        userSelect: 'none',
        '&:hover': {
            borderColor: '#b0b8c1',
        },
    },
    allDayToggleActive: {
        borderColor: theme.palette.primary.main,
        backgroundColor: `${theme.palette.primary.main}08`,
    },
    attachmentZone: {
        border: `2px dashed ${BORDER}`,
        borderRadius: 10,
        padding: theme.spacing(2),
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        cursor: 'pointer',
        transition: 'border-color 0.15s, background-color 0.15s',
        '&:hover': {
            borderColor: theme.palette.primary.light,
            backgroundColor: `${theme.palette.primary.main}04`,
        },
    },
}))

interface PropertyEventModalProps {
    open: boolean
    onClose: () => void
    event: PropertyEvent | null
    initialDate: DateTime | null
    timezone: string
    createEvent: UseMutationResult<
        PropertyEvent,
        unknown,
        CreatePropertyEventInput
    >
    updateEvent: UseMutationResult<
        PropertyEvent,
        unknown,
        UpdatePropertyEventInput & { id: number }
    >
    deleteEvent: UseMutationResult<void, unknown, number>
    draftMessage: UseMutationResult<DraftMessageResponse, unknown, number>
    sendEventMessage: UseMutationResult<
        TenantMessage,
        unknown,
        SendEventMessageInput & { id: number }
    >
    onEventUpdated?: (updated: PropertyEvent) => void
}

interface FormState {
    title: string
    description: string
    location: string
    startsAt: DateTime | null
    endsAt: DateTime | null
    isAllDay: boolean
    status: 'DRAFT' | 'PUBLISHED'
    newAttachments: File[]
    removeAttachmentIds: number[]
}

const defaultForm = (
    initialDate: DateTime | null,
    timezone: string,
): FormState => {
    const start = initialDate
        ? initialDate.setZone(timezone, { keepLocalTime: true })
        : DateTime.now().setZone(timezone)
    return {
        title: '',
        description: '',
        location: '',
        startsAt: start,
        endsAt: start.plus({ hours: 1 }),
        isAllDay: false,
        status: 'DRAFT',
        newAttachments: [],
        removeAttachmentIds: [],
    }
}

const eventToForm = (event: PropertyEvent, timezone: string): FormState => ({
    title: event.title,
    description: event.description || '',
    location: event.location || '',
    startsAt: DateTime.fromISO(event.starts_at).setZone(timezone),
    endsAt: DateTime.fromISO(event.ends_at).setZone(timezone),
    isAllDay: event.is_all_day,
    status: event.status,
    newAttachments: [],
    removeAttachmentIds: [],
})

const toPickerDate = (dt: DateTime | null): Date | null => {
    if (!dt) return null
    return dt.setZone('local', { keepLocalTime: true }).toJSDate()
}

const fromPickerDate = (date: Date, timezone: string): DateTime => {
    return DateTime.fromJSDate(date).setZone(timezone, { keepLocalTime: true })
}

export const PropertyEventModal: React.FC<PropertyEventModalProps> = ({
    open,
    onClose,
    event,
    initialDate,
    timezone,
    createEvent,
    updateEvent,
    deleteEvent,
    draftMessage,
    sendEventMessage,
    onEventUpdated,
}) => {
    const classes = useStyles()
    const isEdit = !!event

    const [form, setForm] = useState<FormState>(
        defaultForm(initialDate, timezone),
    )
    const [confirmDelete, setConfirmDelete] = useState(false)
    const [announcementOpen, setAnnouncementOpen] = useState(false)

    useEffect(() => {
        if (open) {
            if (event) {
                setForm(eventToForm(event, timezone))
            } else {
                setForm(defaultForm(initialDate, timezone))
            }
            setConfirmDelete(false)
            setAnnouncementOpen(false)
        }
    }, [open, event, initialDate, timezone])

    const handleChange = (field: 'title' | 'description' | 'location') => (
        e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }))
    }

    const handleFileAdd = (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = e.target.files
        if (files) {
            setForm((prev) => ({
                ...prev,
                newAttachments: [...prev.newAttachments, ...Array.from(files)],
            }))
        }
        e.target.value = ''
    }

    const handleRemoveNewFile = (index: number) => {
        setForm((prev) => ({
            ...prev,
            newAttachments: prev.newAttachments.filter((_, i) => i !== index),
        }))
    }

    const handleRemoveExistingAttachment = (id: number) => {
        setForm((prev) => ({
            ...prev,
            removeAttachmentIds: [...prev.removeAttachmentIds, id],
        }))
    }

    const existingAttachments: PropertyEventAttachment[] = event
        ? event.attachments.filter(
              (a) => !form.removeAttachmentIds.includes(a.id),
          )
        : []

    const handleSubmit = () => {
        if (!form.title.trim() || !form.startsAt || !form.endsAt) return

        const startsAtUtc = form.startsAt.toUTC().toISO()!
        const endsAtUtc = form.endsAt.toUTC().toISO()!

        if (isEdit) {
            updateEvent.mutate(
                {
                    id: event!.id,
                    title: form.title,
                    description: form.description,
                    location: form.location,
                    starts_at: startsAtUtc,
                    ends_at: endsAtUtc,
                    is_all_day: form.isAllDay,
                    status: form.status,
                    attachments:
                        form.newAttachments.length > 0
                            ? form.newAttachments
                            : undefined,
                    remove_attachment_ids:
                        form.removeAttachmentIds.length > 0
                            ? form.removeAttachmentIds
                            : undefined,
                },
                { onSuccess: onClose },
            )
        } else {
            createEvent.mutate(
                {
                    title: form.title,
                    description: form.description || undefined,
                    location: form.location || undefined,
                    starts_at: startsAtUtc,
                    ends_at: endsAtUtc,
                    is_all_day: form.isAllDay,
                    status: form.status,
                    attachments:
                        form.newAttachments.length > 0
                            ? form.newAttachments
                            : undefined,
                },
                { onSuccess: onClose },
            )
        }
    }

    const handleDelete = () => {
        if (!event) return
        deleteEvent.mutate(event.id, { onSuccess: onClose })
    }

    const isSaving = createEvent.isLoading || updateEvent.isLoading
    const isDeleting = deleteEvent.isLoading

    const endBeforeStart =
        form.startsAt && form.endsAt && form.endsAt < form.startsAt

    return (
        <>
            <Modal
                open={open}
                onClose={(_e: object, reason: string) => {
                    if (
                        reason === 'backdropClick' &&
                        (isSaving || isDeleting || announcementOpen)
                    ) {
                        return
                    }
                    onClose()
                }}
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}
            >
                <Slide direction="up" in={open}>
                    <Paper className={classes.paper}>
                        {/* Header */}
                        <Box className={classes.header}>
                            <Box className={classes.headerIcon}>
                                <EventIcon />
                            </Box>
                            <Box flex={1}>
                                <Typography
                                    style={{
                                        fontWeight: 700,
                                        fontSize: '1.05rem',
                                        lineHeight: 1.2,
                                    }}
                                >
                                    {isEdit ? 'Edit Event' : 'Create New Event'}
                                </Typography>
                                <Typography
                                    style={{
                                        fontSize: '0.8rem',
                                        color: '#6b7280',
                                    }}
                                >
                                    {isEdit
                                        ? 'Update your community event details'
                                        : 'Add an event to the community calendar'}
                                </Typography>
                            </Box>
                            <IconButton size="small" onClick={onClose}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        {/* Body */}
                        <Box className={classes.body}>
                            {/* Event Details Section */}
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    Event Details
                                </Typography>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    style={{ gap: 14 }}
                                >
                                    <TextField
                                        label="Event Title"
                                        placeholder="e.g., Pool Party, Office Hours..."
                                        variant="outlined"
                                        size="small"
                                        required
                                        value={form.title}
                                        onChange={handleChange('title')}
                                        error={
                                            !form.title.trim() &&
                                            form.title !== ''
                                        }
                                        helperText={
                                            !form.title.trim() &&
                                            form.title !== ''
                                                ? 'Title is required'
                                                : undefined
                                        }
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Description"
                                        placeholder="What should residents know about this event?"
                                        variant="outlined"
                                        size="small"
                                        multiline
                                        rows={3}
                                        value={form.description}
                                        onChange={handleChange('description')}
                                        fullWidth
                                    />

                                    <TextField
                                        label="Location"
                                        placeholder="e.g., Main Pool Area, Clubhouse..."
                                        variant="outlined"
                                        size="small"
                                        value={form.location}
                                        onChange={handleChange('location')}
                                        inputProps={{ maxLength: 255 }}
                                        fullWidth
                                        InputProps={{
                                            startAdornment: (
                                                <InputAdornment position="start">
                                                    <LocationIcon
                                                        style={{
                                                            fontSize: 20,
                                                            color: '#9ca3af',
                                                        }}
                                                    />
                                                </InputAdornment>
                                            ),
                                        }}
                                    />
                                </Box>
                            </Box>

                            {/* Date & Time Section */}
                            <Box className={classes.section}>
                                <Box
                                    display="flex"
                                    alignItems="center"
                                    justifyContent="space-between"
                                    marginBottom="12px"
                                >
                                    <Typography
                                        className={classes.sectionTitle}
                                        style={{ marginBottom: 0 }}
                                    >
                                        Date & Time
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontSize: '0.72rem',
                                            color: '#9ca3af',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Timezone:{' '}
                                        {DateTime.now()
                                            .setZone(timezone)
                                            .toFormat('ZZZZ')}
                                    </Typography>
                                </Box>

                                <Box
                                    display="flex"
                                    flexDirection="column"
                                    style={{ gap: 14 }}
                                >
                                    <Box
                                        className={`${classes.allDayToggle} ${
                                            form.isAllDay
                                                ? classes.allDayToggleActive
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setForm((prev) => ({
                                                ...prev,
                                                isAllDay: !prev.isAllDay,
                                            }))
                                        }
                                    >
                                        <ScheduleIcon
                                            style={{
                                                fontSize: 20,
                                                color: form.isAllDay
                                                    ? undefined
                                                    : '#9ca3af',
                                            }}
                                            color={
                                                form.isAllDay
                                                    ? 'primary'
                                                    : 'inherit'
                                            }
                                        />
                                        <Typography
                                            variant="body2"
                                            style={{ fontWeight: 500 }}
                                        >
                                            All day event
                                        </Typography>
                                        <Box
                                            style={{
                                                marginLeft: 'auto',
                                                width: 18,
                                                height: 18,
                                                borderRadius: 4,
                                                border: form.isAllDay
                                                    ? 'none'
                                                    : `2px solid ${BORDER}`,
                                                backgroundColor: form.isAllDay
                                                    ? undefined
                                                    : '#fff',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                            }}
                                        >
                                            {form.isAllDay && (
                                                <Box
                                                    style={{
                                                        width: 18,
                                                        height: 18,
                                                        borderRadius: 4,
                                                        backgroundColor:
                                                            '#008C85',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent:
                                                            'center',
                                                        color: '#fff',
                                                        fontSize: 14,
                                                        fontWeight: 700,
                                                    }}
                                                >
                                                    &#10003;
                                                </Box>
                                            )}
                                        </Box>
                                    </Box>

                                    {form.isAllDay ? (
                                        <Box display="flex" style={{ gap: 12 }}>
                                            <KeyboardDatePicker
                                                label="Start Date"
                                                inputVariant="outlined"
                                                size="small"
                                                format="MM/dd/yyyy"
                                                value={toPickerDate(
                                                    form.startsAt,
                                                )}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            startsAt: fromPickerDate(
                                                                date,
                                                                timezone,
                                                            ),
                                                        }))
                                                    }
                                                }}
                                                fullWidth
                                                KeyboardButtonProps={{
                                                    'aria-label':
                                                        'change start date',
                                                }}
                                            />
                                            <KeyboardDatePicker
                                                label="End Date"
                                                inputVariant="outlined"
                                                size="small"
                                                format="MM/dd/yyyy"
                                                value={toPickerDate(
                                                    form.endsAt,
                                                )}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            endsAt: fromPickerDate(
                                                                date,
                                                                timezone,
                                                            ),
                                                        }))
                                                    }
                                                }}
                                                fullWidth
                                                error={!!endBeforeStart}
                                                helperText={
                                                    endBeforeStart
                                                        ? 'End must be on or after start'
                                                        : undefined
                                                }
                                                KeyboardButtonProps={{
                                                    'aria-label':
                                                        'change end date',
                                                }}
                                            />
                                        </Box>
                                    ) : (
                                        <Box display="flex" style={{ gap: 12 }}>
                                            <KeyboardDateTimePicker
                                                label="Start"
                                                inputVariant="outlined"
                                                size="small"
                                                format="MM/dd/yyyy hh:mm a"
                                                value={toPickerDate(
                                                    form.startsAt,
                                                )}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            startsAt: fromPickerDate(
                                                                date,
                                                                timezone,
                                                            ),
                                                        }))
                                                    }
                                                }}
                                                fullWidth
                                                KeyboardButtonProps={{
                                                    'aria-label':
                                                        'change start time',
                                                }}
                                            />
                                            <KeyboardDateTimePicker
                                                label="End"
                                                inputVariant="outlined"
                                                size="small"
                                                format="MM/dd/yyyy hh:mm a"
                                                value={toPickerDate(
                                                    form.endsAt,
                                                )}
                                                onChange={(date) => {
                                                    if (date) {
                                                        setForm((prev) => ({
                                                            ...prev,
                                                            endsAt: fromPickerDate(
                                                                date,
                                                                timezone,
                                                            ),
                                                        }))
                                                    }
                                                }}
                                                fullWidth
                                                error={!!endBeforeStart}
                                                helperText={
                                                    endBeforeStart
                                                        ? 'End must be after start'
                                                        : undefined
                                                }
                                                KeyboardButtonProps={{
                                                    'aria-label':
                                                        'change end time',
                                                }}
                                            />
                                        </Box>
                                    )}
                                </Box>
                            </Box>

                            {/* Visibility Section */}
                            <Box className={classes.section}>
                                <Typography className={classes.sectionTitle}>
                                    Visibility
                                </Typography>

                                <Box className={classes.statusToggle}>
                                    <Box
                                        className={`${classes.statusOption} ${
                                            form.status === 'DRAFT'
                                                ? classes.statusOptionActive
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setForm((prev) => ({
                                                ...prev,
                                                status: 'DRAFT',
                                            }))
                                        }
                                    >
                                        <DraftIcon
                                            style={{ fontSize: 20 }}
                                            color={
                                                form.status === 'DRAFT'
                                                    ? 'primary'
                                                    : 'disabled'
                                            }
                                        />
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                Draft
                                            </Typography>
                                            <Typography
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: '#6b7280',
                                                }}
                                            >
                                                Only visible to staff
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <Box
                                        className={`${classes.statusOption} ${
                                            form.status === 'PUBLISHED'
                                                ? classes.statusOptionActive
                                                : ''
                                        }`}
                                        onClick={() =>
                                            setForm((prev) => ({
                                                ...prev,
                                                status: 'PUBLISHED',
                                            }))
                                        }
                                    >
                                        <PublishIcon
                                            style={{ fontSize: 20 }}
                                            color={
                                                form.status === 'PUBLISHED'
                                                    ? 'primary'
                                                    : 'disabled'
                                            }
                                        />
                                        <Box>
                                            <Typography
                                                variant="body2"
                                                style={{
                                                    fontWeight: 600,
                                                    lineHeight: 1.2,
                                                }}
                                            >
                                                Published
                                            </Typography>
                                            <Typography
                                                style={{
                                                    fontSize: '0.7rem',
                                                    color: '#6b7280',
                                                }}
                                            >
                                                Visible to residents
                                            </Typography>
                                        </Box>
                                    </Box>
                                </Box>
                            </Box>

                            {/* Announcements Section (edit only) */}
                            {isEdit && event && (
                                <Box className={classes.section}>
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        justifyContent="space-between"
                                        marginBottom="12px"
                                    >
                                        <Typography
                                            className={classes.sectionTitle}
                                            style={{ marginBottom: 0 }}
                                        >
                                            Announcements
                                        </Typography>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            startIcon={<SendIcon />}
                                            onClick={() =>
                                                setAnnouncementOpen(true)
                                            }
                                            style={{
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 8,
                                            }}
                                        >
                                            Send Announcement
                                        </Button>
                                    </Box>

                                    {(event.messages?.length ?? 0) === 0 ? (
                                        <Typography
                                            style={{
                                                fontSize: '0.8rem',
                                                color: '#6b7280',
                                            }}
                                        >
                                            No announcements sent yet. Draft
                                            with Lori or write your own to
                                            notify residents.
                                        </Typography>
                                    ) : (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            style={{ gap: 8 }}
                                        >
                                            {event.messages!.map((msg) => (
                                                <Box
                                                    key={msg.id}
                                                    display="flex"
                                                    alignItems="center"
                                                    style={{
                                                        gap: 10,
                                                        padding: '10px 12px',
                                                        border: `1px solid ${BORDER}`,
                                                        borderRadius: 8,
                                                        backgroundColor:
                                                            '#fafbfc',
                                                    }}
                                                >
                                                    <MailIcon
                                                        style={{
                                                            fontSize: 18,
                                                            color: '#9ca3af',
                                                        }}
                                                    />
                                                    <Box flex={1} minWidth={0}>
                                                        <Typography
                                                            style={{
                                                                fontWeight: 600,
                                                                fontSize:
                                                                    '0.85rem',
                                                                whiteSpace:
                                                                    'nowrap',
                                                                overflow:
                                                                    'hidden',
                                                                textOverflow:
                                                                    'ellipsis',
                                                            }}
                                                        >
                                                            {msg.subject}
                                                        </Typography>
                                                        <Typography
                                                            style={{
                                                                fontSize:
                                                                    '0.72rem',
                                                                color:
                                                                    '#6b7280',
                                                            }}
                                                        >
                                                            {DateTime.fromISO(
                                                                msg.created_date,
                                                                { zone: 'utc' },
                                                            )
                                                                .setZone(
                                                                    timezone,
                                                                )
                                                                .toFormat(
                                                                    'MMM d, yyyy h:mm a',
                                                                )}{' '}
                                                            ·{' '}
                                                            {
                                                                msg.recipient_count
                                                            }{' '}
                                                            recipient
                                                            {msg.recipient_count !==
                                                            1
                                                                ? 's'
                                                                : ''}
                                                        </Typography>
                                                    </Box>
                                                </Box>
                                            ))}
                                        </Box>
                                    )}
                                </Box>
                            )}

                            {/* Attachments Section */}
                            <Box
                                className={classes.section}
                                style={{ marginBottom: 0 }}
                            >
                                <Typography className={classes.sectionTitle}>
                                    Attachments
                                </Typography>

                                {(existingAttachments.length > 0 ||
                                    form.newAttachments.length > 0) && (
                                    <Box
                                        display="flex"
                                        flexWrap="wrap"
                                        style={{ gap: 8, marginBottom: 12 }}
                                    >
                                        {existingAttachments.map((att) => (
                                            <Chip
                                                key={att.id}
                                                icon={<AttachFileIcon />}
                                                label={att.name}
                                                onDelete={() =>
                                                    handleRemoveExistingAttachment(
                                                        att.id,
                                                    )
                                                }
                                                variant="outlined"
                                                size="small"
                                                clickable
                                                component="a"
                                                href={att.file_url}
                                                target="_blank"
                                            />
                                        ))}
                                        {form.newAttachments.map(
                                            (file, idx) => (
                                                <Chip
                                                    key={`new-${idx}`}
                                                    icon={<UploadIcon />}
                                                    label={file.name}
                                                    onDelete={() =>
                                                        handleRemoveNewFile(idx)
                                                    }
                                                    variant="outlined"
                                                    size="small"
                                                    color="primary"
                                                />
                                            ),
                                        )}
                                    </Box>
                                )}

                                <Box
                                    className={classes.attachmentZone}
                                    component="label"
                                >
                                    <UploadIcon
                                        style={{
                                            fontSize: 28,
                                            color: '#9ca3af',
                                            marginBottom: 4,
                                        }}
                                    />
                                    <Typography
                                        variant="body2"
                                        style={{
                                            color: '#6b7280',
                                            fontWeight: 500,
                                        }}
                                    >
                                        Click to upload files
                                    </Typography>
                                    <Typography
                                        style={{
                                            fontSize: '0.7rem',
                                            color: '#9ca3af',
                                        }}
                                    >
                                        PDF, images, or documents
                                    </Typography>
                                    <input
                                        type="file"
                                        hidden
                                        multiple
                                        onChange={handleFileAdd}
                                    />
                                </Box>
                            </Box>
                        </Box>

                        {/* Footer */}
                        <Box className={classes.footer}>
                            <Box>
                                {isEdit && !confirmDelete && (
                                    <Tooltip title="Permanently remove this event">
                                        <Button
                                            className={classes.deleteButton}
                                            startIcon={<DeleteIcon />}
                                            onClick={() =>
                                                setConfirmDelete(true)
                                            }
                                            disabled={isDeleting}
                                            size="small"
                                        >
                                            Delete
                                        </Button>
                                    </Tooltip>
                                )}
                                {isEdit && confirmDelete && (
                                    <Box
                                        display="flex"
                                        alignItems="center"
                                        style={{ gap: 8 }}
                                    >
                                        <Typography
                                            variant="body2"
                                            color="error"
                                            style={{ fontWeight: 500 }}
                                        >
                                            Are you sure?
                                        </Typography>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            style={{
                                                backgroundColor: '#ef4444',
                                                color: '#fff',
                                                textTransform: 'none',
                                                fontWeight: 600,
                                                borderRadius: 6,
                                                boxShadow: 'none',
                                            }}
                                            onClick={handleDelete}
                                            disabled={isDeleting}
                                        >
                                            {isDeleting
                                                ? 'Deleting...'
                                                : 'Yes, delete'}
                                        </Button>
                                        <Button
                                            size="small"
                                            style={{
                                                textTransform: 'none',
                                                borderRadius: 6,
                                            }}
                                            onClick={() =>
                                                setConfirmDelete(false)
                                            }
                                        >
                                            No
                                        </Button>
                                    </Box>
                                )}
                            </Box>

                            <Box display="flex" style={{ gap: 8 }}>
                                <Button
                                    className={classes.cancelButton}
                                    onClick={onClose}
                                    disabled={isSaving}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    variant="contained"
                                    color="primary"
                                    className={classes.primaryButton}
                                    onClick={handleSubmit}
                                    disabled={
                                        isSaving ||
                                        !form.title.trim() ||
                                        !form.startsAt ||
                                        !form.endsAt ||
                                        !!endBeforeStart
                                    }
                                >
                                    {isSaving
                                        ? 'Saving...'
                                        : isEdit
                                        ? 'Save Changes'
                                        : 'Create Event'}
                                </Button>
                            </Box>
                        </Box>
                    </Paper>
                </Slide>
            </Modal>

            {event && (
                <EventAnnouncementModal
                    open={announcementOpen}
                    onClose={() => setAnnouncementOpen(false)}
                    eventId={event.id}
                    eventTitle={event.title}
                    draftMessage={draftMessage}
                    sendEventMessage={sendEventMessage}
                    onSent={(msg) => {
                        const newSummary: EventMessageSummary = {
                            id: msg.id,
                            subject: msg.subject,
                            created_date: msg.created_date,
                            recipient_count: msg.recipient_count,
                        }
                        const updated: PropertyEvent = {
                            ...event,
                            messages: [...(event.messages ?? []), newSummary],
                        }
                        onEventUpdated?.(updated)
                    }}
                />
            )}
        </>
    )
}
